import { ErrorMessage } from '@/components/loading-error';
import { useVideoEngine } from '@/hooks/useVideoEngine';
import {
  getCustomLanguageFromURL,
  getLanguageConfig,
} from '@/utils/customLanguageUtils';
import { HEYGEN_AVATARS, ELEVEN_LABS_JAMES_VOICE_ID } from '@/utils/constants';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  X,
  ShoppingBag,
  AlertCircle,
  PhoneOff,
  Video,
  VideoOff,
  Mic,
  MicOff,
  MessageSquare,
  TrendingUp,
  Users,
  BarChart3,
  Clock,
  ChevronRight,
  Sparkles,
  Building2,
  Target,
  BookOpen,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

// Components
import { LoadingOverlay } from '@/components/ui/loading';
import { Button } from '@/components/ui/button';

// Store & Hooks
import api from '@/utils/ky';
import { useCoachStore } from '@/store/coach';

// Video-specific components (we'll create these)
import { VideoSetup } from './components/VideoSetup';
import { VideoDisplay } from './components/VideoDisplay';
import { ConversationPanel } from './components/ConversationPanel';
import { FeedbackAnalysis } from './components/FeedbackAnalysis';
import { ResourcePanel } from './components/ResourcePanel';

export function VideoRoleplay() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scenarioCode = searchParams.get('scenario');
  const engine = useMemo(() => {
    const engineParam = searchParams.get('engine') as
      | 'heygen'
      | 'livekit'
      | null;
    return engineParam && ['heygen', 'livekit'].includes(engineParam)
      ? engineParam
      : null;
  }, [searchParams]) as 'heygen' | 'livekit' | null;
  const { i18n } = useTranslation();
  const coachStore = useCoachStore();

  const currentLanguage = getCustomLanguageFromURL();
  const languageConfig = getLanguageConfig(currentLanguage);

  useEffect(() => {
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, i18n]);

  const [loadingState, setLoadingState] = useState<
    'initial' | 'loading' | 'setup' | 'ready' | 'active' | 'ending' | 'error'
  >('initial');
  const [error, setError] = useState<string | null>(null);
  const [scenario, setScenario] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isPracticeStarted, setIsPracticeStarted] = useState(false);
  const [hasCompetitorData, setHasCompetitorData] = useState(false);
  const [competitorMentioned, setCompetitorMentioned] = useState(false);
  const [selectedResourceTab, setSelectedResourceTab] = useState<
    'overview' | 'product' | 'framework' | 'competitors'
  >('overview');
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isMicEnabled, setIsMicEnabled] = useState(true);
  const [messages, setMessages] = useState<
    Array<{
      id: string;
      source: 'ai' | 'user';
      message: string;
      timestamp: Date;
    }>
  >([]);
  const [isConversationOpen, setIsConversationOpen] = useState(false);

  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);

  const videoEngine = useVideoEngine(engine);

  const videoEngineRef = useRef(videoEngine);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    videoEngineRef.current = videoEngine;
  }, [videoEngine]);

  useEffect(() => {
    return () => {
      const currentEngine = videoEngineRef.current;
      if (currentEngine) {
        currentEngine.stop();
      }
    };
  }, []);

  useEffect(() => {
    const fetchScenario = async () => {
      if (!scenarioCode) {
        setError('No scenario specified');
        setLoadingState('error');
        return;
      }

      setLoadingState('loading');

      try {
        const response = await api
          .get(
            `api/custom/scenarios/${scenarioCode}?language=${currentLanguage}`,
          )
          .json<any>();

        if (!response) {
          throw new Error('Invalid scenario response');
        }

        setScenario(response);

        const hasCompetitors =
          response.competitors && response.competitors.length > 0;
        setHasCompetitorData(hasCompetitors);

        setLoadingState('setup');
      } catch (err) {
        console.error('Error loading scenario:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load scenario',
        );
        setLoadingState('error');
      }
    };

    fetchScenario();
  }, [scenarioCode, currentLanguage]);

  const handleConnect = useCallback(() => {
    startTimeRef.current = Date.now();
    setLoadingState('active');
    setIsPracticeStarted(true);
  }, []);

  const handleError = useCallback((error: any) => {
    console.error('Video engine error:', error);
    toast.error('Connection error: ' + (error.message || 'Unknown error'));
  }, []);

  const handleMessage = useCallback(
    (data: { source: 'ai' | 'user'; message: string }) => {
      const newMessage = {
        id: `msg-${Date.now()}`,
        source: data.source,
        message: data.message,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, newMessage]);

      if (
        hasCompetitorData &&
        data.source === 'user' &&
        scenario?.competitors
      ) {
        const mentioned = scenario.competitors.some((competitor: any) =>
          data.message.toLowerCase().includes(competitor.name.toLowerCase()),
        );
        if (mentioned && !competitorMentioned) {
          setCompetitorMentioned(true);
        }
      }
    },
    [hasCompetitorData, competitorMentioned, scenario],
  );

  const handleTranscript = useCallback(
    (transcript: string, isFinal: boolean) => {},
    [],
  );

  const handleStartPractice = useCallback(async () => {
    try {
      setLoadingState('ready');

      const sessionData = await api
        .post('api/custom/sessions', {
          json: {
            userId: coachStore.userId,
            scenarioCode,
            language: currentLanguage,
          },
        })
        .json<{ sessionId: string }>();

      setSessionId(sessionData.sessionId);

      const session = await api
        .get(`api/custom/sessions/${sessionData.sessionId}`)
        .json<any>();

      // Load scenario-specific instructions
      const loadInstructions = () => {
        // Check for HSBC-1 specific instructions
        if (scenarioCode === 'hsbc-1') {
          return getHSBC1Instructions();
        }

        // Check for Manulife-1 specific instructions
        if (scenarioCode === 'manulife-1') {
          return getManulife1Instructions();
        }

        // Check for Manulife-2 specific instructions (failed scenario)
        if (scenarioCode === 'manulife-2') {
          return getManulife2Instructions();
        }

        // Default instructions fallback
        return (
          session.voiceInstructions?.chat ||
          scenario?.scenario?.context ||
          scenario?.persona_instruction ||
          `You are a customer interested in ${
            scenario?.scenario?.product?.name ||
            scenario?.product_name ||
            'our products'
          }.`
        );
      };

      const instructions = loadInstructions();

      const conversationId = await videoEngine.start(
        {
          avatarId: scenario?.hedraAvatarId || HEYGEN_AVATARS.WAYNE,
          voiceId: session.aiConfig?.voiceId || ELEVEN_LABS_JAMES_VOICE_ID,
          instructions,
          language: currentLanguage,
          quality: 'low',
          roomName:
            videoEngine.engine === 'livekit'
              ? `avatar-${sessionData.sessionId}`
              : sessionData.sessionId,
          participantName: `user-${coachStore.userId || 'anonymous'}`,
          avatarImage: scenario?.hedraAvatarImage,
          firstMessage:
            scenarioCode === 'manulife-1' || scenarioCode === 'manulife-2'
              ? '' // Jake should not speak first, advisor initiates
              : scenario?.persona?.initialMessage ||
                'Hello, nice to see you again.',
        },
        {
          onConnect: handleConnect,
          onError: handleError,
          onMessage: handleMessage,
          onTranscript: handleTranscript,
          onAgentSpeaking: (isSpeaking) => {},
        },
      );
    } catch (err) {
      console.error('Failed to start practice:', err);
      setError(err instanceof Error ? err.message : 'Failed to start practice');
      setLoadingState('error');
    }
  }, [
    scenario,
    videoEngine,
    currentLanguage,
    handleConnect,
    handleError,
    handleMessage,
    handleTranscript,
    scenarioCode,
    coachStore,
  ]);

  const handleEndPractice = useCallback(async () => {
    setLoadingState('ending');
    setIsLoadingFeedback(true);

    try {
      await videoEngine.stop();

      // Add fake loading timer (1-3 seconds) for better UX
      const loadingDelay = Math.random() * 2000 + 1000; // Random between 1-3 seconds
      await new Promise((resolve) => setTimeout(resolve, loadingDelay));

      if (messages.length > 0 && sessionId) {
        const formattedTranscript = messages
          .map(
            (msg) =>
              `${msg.source === 'ai' ? 'Client' : 'Salesperson'}: ${
                msg.message
              }`,
          )
          .join('\n');

        const feedbackResponse = await api
          .post(`api/custom/sessions/${sessionId}/feedback`, {
            json: {
              transcript: formattedTranscript,
              duration: (Date.now() - startTimeRef.current) / 1000,
              hasCompetitorData,
              competitorMentioned,
              language: currentLanguage,
              scenarioCode, // Add scenario code to help backend generate specific feedback
            },
          })
          .json<any>();

        // For Manulife scenarios, enhance feedback with specific insights
        if (scenarioCode === 'manulife-1') {
          const enhancedFeedback = enhanceManulifeFeedback(
            feedbackResponse,
            messages,
          );
          setFeedbackData(enhancedFeedback);
        } else if (scenarioCode === 'manulife-2') {
          const enhancedFeedback = enhanceManulife2Feedback(
            feedbackResponse,
            messages,
          );
          setFeedbackData(enhancedFeedback);
        } else {
          setFeedbackData(feedbackResponse);
        }
      }

      setShowFeedback(true);
    } catch (err) {
      console.error('Error ending practice:', err);
      toast.error('Failed to generate feedback');
    } finally {
      setIsLoadingFeedback(false);
    }
  }, [
    videoEngine,
    messages,
    sessionId,
    hasCompetitorData,
    competitorMentioned,
    currentLanguage,
    scenarioCode,
  ]);

  const toggleVideo = useCallback(() => {
    setIsVideoEnabled((prev) => !prev);
  }, []);

  const toggleMic = useCallback(() => {
    setIsMicEnabled((prev) => !prev);
  }, []);

  const handleBack = () => {
    if (isPracticeStarted) {
      if (
        confirm(
          'Are you sure you want to leave? Your practice session will end.',
        )
      ) {
        handleEndPractice();
      }
    } else {
      navigate(-1);
    }
  };

  if (loadingState === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <ErrorMessage
          message={error || 'Something went wrong'}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  if (loadingState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingOverlay state="loading" />
      </div>
    );
  }

  if (loadingState === 'setup') {
    return (
      <VideoSetup
        scenario={scenario}
        onStart={handleStartPractice}
        onBack={() => navigate(-1)}
      />
    );
  }

  if (showFeedback) {
    return (
      <FeedbackAnalysis
        feedbackData={feedbackData}
        isLoading={isLoadingFeedback}
        onClose={() => navigate(-1)}
      />
    );
  }

  return (
    <div className="h-screen flex bg-gray-50">
      {/* Left Sidebar - Resources */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Resources</h3>
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-gray-500">Live</span>
            </div>
          </div>

          {/* Resource Tabs */}
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setSelectedResourceTab('overview')}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                selectedResourceTab === 'overview'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setSelectedResourceTab('product')}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                selectedResourceTab === 'product'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Product
            </button>
            <button
              onClick={() => setSelectedResourceTab('framework')}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                selectedResourceTab === 'framework'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Framework
            </button>
            {hasCompetitorData && (
              <button
                onClick={() => setSelectedResourceTab('competitors')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  selectedResourceTab === 'competitors'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                } ${
                  competitorMentioned ? 'animate-pulse text-orange-600' : ''
                }`}
              >
                Compete
              </button>
            )}
          </div>
        </div>

        {/* Resource Content */}
        <ResourcePanel
          selectedTab={selectedResourceTab}
          scenario={scenario}
          competitorMentioned={competitorMentioned}
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">
                {scenario?.scenario?.product?.company || scenario?.company_name}{' '}
                • {scenario?.scenario?.product?.name || scenario?.product_name}
              </span>
            </div>

            <div className="flex items-center space-x-3">
              {/* Call Duration */}
              <div className="flex items-center space-x-1.5 px-3 py-1.5 bg-gray-50 rounded-lg">
                <Clock className="h-3.5 w-3.5 text-gray-500" />
                <span className="text-sm font-mono text-gray-700">
                  {new Date(Date.now() - startTimeRef.current)
                    .toISOString()
                    .substr(14, 5)}
                </span>
              </div>

              {/* Controls */}
              <button
                onClick={toggleVideo}
                className={`p-2 rounded-lg transition-all ${
                  isVideoEnabled
                    ? 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {isVideoEnabled ? (
                  <Video className="h-4 w-4" />
                ) : (
                  <VideoOff className="h-4 w-4" />
                )}
              </button>

              <button
                onClick={toggleMic}
                className={`p-2 rounded-lg transition-all ${
                  isMicEnabled
                    ? 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {isMicEnabled ? (
                  <Mic className="h-4 w-4" />
                ) : (
                  <MicOff className="h-4 w-4" />
                )}
              </button>

              <div className="h-8 w-px bg-gray-200"></div>

              <button
                onClick={handleEndPractice}
                disabled={loadingState === 'ending'}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black transition-colors disabled:opacity-50"
              >
                <PhoneOff className="h-4 w-4" />
                <span className="text-sm font-medium">End</span>
              </button>
            </div>
          </div>
        </div>

        {/* Video Area */}
        <VideoDisplay
          videoRef={videoEngine.videoRef}
          userVideoRef={videoEngine.userVideoRef}
          isConnected={videoEngine.status === 'connected'}
          isSpeaking={videoEngine.isSpeaking}
          isRecording={videoEngine.isRecording}
        />
      </div>

      {/* Right Sidebar - Conversation */}
      <AnimatePresence>
        {isConversationOpen && (
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: 384 }}
            exit={{ width: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white border-l border-gray-200"
          >
            <ConversationPanel
              messages={messages}
              isRecording={videoEngine.isRecording}
              isSpeaking={videoEngine.isSpeaking}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Conversation Toggle Button */}
      <button
        onClick={() => setIsConversationOpen(!isConversationOpen)}
        className="absolute top-20 right-4 z-10 p-2 bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50 transition-all"
      >
        <MessageSquare className="h-4 w-4 text-gray-600" />
        {messages.length > 0 && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
        )}
      </button>
    </div>
  );
}

// HSBC-1 Scenario Instructions (kept separate for clarity)
function getHSBC1Instructions() {
  return `Interactive Avatar Instructions - James Richardson (Client)

YOUR IDENTITY
You are James Richardson, a 42-year-old tech entrepreneur who recently sold your AI-chip design company, QuantumForge Pte Ltd, for £30 million. You're currently evaluating whether to move some of your wealth to this financial advisor's firm. This is your SECOND meeting with them.

YOUR CURRENT FINANCIAL SITUATION
- £16.8m with Global Bank A (ETFs, USD money market funds, cash)
- £8.1m with Boutique Bank B (private equity, venture debt notes)
- £5.1m with Digital Platform C (crypto ETPs, robo-advisor)
- £5m in escrow (releasing August 2025)

YOUR PERSONALITY & COMMUNICATION STYLE
- Analytical and data-driven (you built a tech company)
- Direct communicator - you don't beat around the bush
- Skeptical but fair - you'll listen but need convincing
- Value-focused - you care about fees and real returns
- Experienced - you've dealt with many financial advisors before

MEETING CONTEXT
- This is meeting #2 - you've already heard their initial pitch
- You've done your research since the last meeting
- You have specific questions and concerns
- You're NOT sold yet - you need convincing

HOW TO START THE CONVERSATION
Begin with a simple greeting like:
- "Hello again"
- "Hi, thanks for meeting with me again"
- "Good to see you"

Then WAIT for the advisor to lead. Don't launch into questions immediately.

YOUR MAIN CONCERNS & QUESTIONS

Primary Concern:
"I already have my assets diversified across three reputable institutions. What specific value would your firm add that I'm not already getting?"

Key Areas to Probe:
1. Fees: "What exactly are your all-in costs? Include everything."
2. Performance: "Show me actual client returns, not hypotheticals."
3. Strategy: "What specific approach would you take with my portfolio?"
4. Access: "What exclusive opportunities do you actually have?"
5. Service: "How often would we meet? Who's my actual contact?"

YOUR BEHAVIORAL GUIDELINES

Show Realistic Skepticism:
- Challenge vague claims: "That sounds generic. Can you be specific?"
- Push for details: "What does 'holistic approach' actually mean?"
- Question fees: "That seems high compared to my current 0.5% all-in"

Express Frustration When Appropriate:
- If they can't answer: "You don't know your own fee structure?"
- If they're vague: "This is pretty basic stuff you should know"
- If they deflect: "That doesn't answer my question"

Natural Reactions:
- Acknowledge good points: "Okay, that's interesting"
- Show when you're thinking: "Hmm, let me think about that"
- Express doubt: "I'm not convinced that justifies the higher fees"
- Be direct: "I don't think I need that service"

TOPICS THAT ANNOY YOU
- Generic wealth management pitches
- Vague promises without specifics
- Inability to explain fees clearly
- Not knowing their own products
- Pushy closing tactics

HOW YOU MAKE DECISIONS
- You need specific data and examples
- You compare everything to your current setup
- You value transparency and directness
- You don't make emotional decisions
- You always sleep on big financial decisions

NATURAL WAYS TO END THE CONVERSATION

If Interested but Not Ready:
- "I need to think about this more"
- "Send me the detailed fee breakdown"
- "Let me run the numbers myself"

If Not Interested:
- "I don't think this is for me"
- "The fees don't justify the value"
- "I'm good with my current setup"

General Endings:
- "I think we've covered enough for today"
- "Thanks for your time"
- "I'll be in touch if I want to proceed"

IMPORTANT BEHAVIORAL RULES
- You're the CLIENT evaluating them, not helping them
- Don't be overly polite - be professional but direct
- Show impatience with poor answers
- You value YOUR time - don't let them waste it
- Ask follow-up questions when answers are unclear
- Don't accept jargon without explanation
- Remember: you're successful and don't need to impress them

EXAMPLE INTERACTIONS

Advisor gives vague answer about returns:
You: "That's pretty vague. I asked for specific numbers. What was the actual average return for clients like me over the past 3 years?"

Advisor pitches a product:
You: "How does this compare to the ETF portfolio I already have? And what's the total cost difference?"

Advisor can't answer a question:
You: "This is concerning. These are basic details about your own services. Should I be talking to someone else?"

REMEMBER
- You built and sold a tech company - you're smart and analytical
- You don't need their services - they need to convince you
- You have plenty of options - you're interviewing them
- Be skeptical but fair - give them a chance to prove value
- Your time is valuable - don't let them waste it`;
}

// Manulife-1 Scenario Instructions
function getManulife1Instructions() {
  return `Interactive Avatar Instructions - Jake Reyes (Client)

YOUR IDENTITY
You are Jake Reyes, a successful car dealer. This is your FIRST meeting with a Manulife Financial Advisor (FA) named Misha Santos.

YOUR PERSONALITY & COMMUNICATION STYLE
- Friendly but cautious with financial matters
- Value family time and work-life balance
- Ask clarifying questions when curious
- Appreciate stability and long-term planning
- Open to learning but need clear explanations

YOUR CONVERSATION RESPONSES
You must respond exactly as scripted below based on what the advisor says. Follow this script precisely:

When greeted:
"Hello."

When asked to start the meeting:
"Ok, let us start."

When asked what you do for a living:
"I'm into buying and selling cars."

When asked about managing work and family:
"This is much better than being in corporate, frankly. Right now, I am mostly at home, and I spend more time with my family now. I only head out when I need to meet customers to check or buy or sell a car."

When they mention precious family moments:
"Totally agree with you on this one."

When asked about regular meetings:
"Wow, and you do all these regularly?"

When asked if this is a one-time meetup:
"So this is not a 1-time meetup?"

When they confirm ongoing support:
"This is amazing."

When asked to proceed:
"Yes, let us proceed"

When they mention company stability (100+ years):
"That is great to hear. Knowing that the company has been in existence for more than 100 years assures me of your stability."

When they mention social responsibility:
"The stability of the company is one thing that I value the most and the partnership with organizations that pays it forward to the society. I am now very happy and excited that I have met you today."

When asked about your life stage:
"My wife and I got married last year."

When congratulated on marriage:
"It was more than I dreamt of. It was our start of forever and I guess, that places me in the young family stage."

When asked about financial goals:
"The goal right now is to protect that family's finances, health coverage for every member of the family as well as enough funds for my children's education. Oh, let me add a convenient retirement as well."

When asked if you agree about planning:
"Definitely."

When asked which quarter of life you're in:
"My wife and I are both in quarter 2."

When they explain about 14,000 days:
"I never really realized this until you asked this of me. I understand that this would be quarter 2 and quarter 3 in the circle of life."

When asked about which fears resonate:
"My main fear is the survivability of the family which includes the fear of being sick and being suddenly taken out of the picture. Equal to this fear is the future of my child. And while this is years away, retirement usually crosses my mind every now and then."

When they ask if you agree to plan:
"Definitely"

When asked about hierarchy of financial needs:
"Not really. All I was advised of is to save money for emergencies and purchase assets like real estate."

When they explain the hierarchy:
"I thought savings and having assets is enough. Now, I am very curious as to what is this Hierarchy."

When asked where you are in the pyramid:
"I guess it would still be at the foundation since I only have an emergency fund. Which, I would admit, is not even allotted for something specific such as health."

When they mention you're not alone:
"Definitely."

When they mention friends' experiences:
"That is right! Sadly, I know quite a handful of friends who have experienced that."

When reflecting on starting sooner:
"Looking back, I should have started sooner. I'm really glad that we are having this conversation."

When asked about children's education:
"While giving my children the ability to choose their major, I would like them to be in the best school in the country."

When they mention parental responsibility:
"I agree with this. It is the responsibility of the parents to plan for their children's future."

When asked about retirement savings:
"Is my savings not enough for retirement?"

When asked if you have questions:
"So far so good. Everything is explained clearly."

When asked which needs resonate most:
"I would like to plan for everything–Income Protection, Health, Education, and Retirement. My family deserves the best."

IMPORTANT BEHAVIORAL RULES
- Stay on script - use the exact responses provided
- Don't add extra details or deviate from the script
- Keep responses natural but precise
- Show genuine interest in financial planning
- Be warm and receptive to the advisor's guidance`;
}

// Enhanced feedback for Manulife scenario
function enhanceManulifeFeedback(baseFeedback: any, messages: any[]) {
  const clientResponses = messages
    .filter((m) => m.source === 'ai')
    .map((m) => m.message);
  const advisorMessages = messages
    .filter((m) => m.source === 'user')
    .map((m) => m.message);

  // Calculate performance based on conversation flow
  const expectedResponsesCount = 31; // Total scripted responses
  const actualResponsesCount = clientResponses.length;
  const responseAccuracy = Math.min(
    (actualResponsesCount / expectedResponsesCount) * 100,
    100,
  );

  // Analyze key conversation points
  const keyPointsCovered = {
    introduction: advisorMessages.some(
      (m) =>
        m.toLowerCase().includes('misha santos') ||
        m.toLowerCase().includes('financial advisor'),
    ),
    companyOverview: advisorMessages.some(
      (m) =>
        m.toLowerCase().includes('manulife') &&
        (m.includes('1887') || m.includes('118 years')),
    ),
    needsAssessment: advisorMessages.some(
      (m) =>
        m.toLowerCase().includes('financial needs') ||
        m.toLowerCase().includes('priorities'),
    ),
    circleOfLife: advisorMessages.some(
      (m) =>
        m.toLowerCase().includes('circle of life') ||
        m.toLowerCase().includes('28,000 days'),
    ),
    hierarchyDiscussion: advisorMessages.some(
      (m) =>
        m.toLowerCase().includes('hierarchy') &&
        m.toLowerCase().includes('financial'),
    ),
    productPresentation: advisorMessages.some(
      (m) =>
        m.toLowerCase().includes('protection') ||
        m.toLowerCase().includes('education') ||
        m.toLowerCase().includes('retirement'),
    ),
  };

  const keyPointsScore =
    (Object.values(keyPointsCovered).filter((v) => v).length /
      Object.keys(keyPointsCovered).length) *
    100;

  // Generate specific strengths based on the conversation
  const strengths = [];
  if (keyPointsCovered.introduction) {
    strengths.push(
      'Excellent introduction and rapport building with Jake Reyes',
    );
  }
  if (keyPointsCovered.companyOverview) {
    strengths.push(
      "Comprehensive presentation of Manulife's history and stability",
    );
  }
  if (keyPointsCovered.needsAssessment) {
    strengths.push(
      "Thorough needs assessment aligned with Jake's young family stage",
    );
  }
  if (keyPointsCovered.circleOfLife) {
    strengths.push(
      'Effective use of the Circle of Life framework to illustrate financial planning stages',
    );
  }
  if (advisorMessages.length > 15) {
    strengths.push(
      'Maintained excellent engagement throughout the lengthy consultation',
    );
  }

  // Generate improvement areas
  const improvements = [];
  if (!keyPointsCovered.hierarchyDiscussion) {
    improvements.push(
      'Ensure to explain the Hierarchy of Financial Needs clearly',
    );
  }
  if (actualResponsesCount < expectedResponsesCount * 0.8) {
    improvements.push(
      'Allow more time for client responses and follow their pace',
    );
  }
  if (
    !advisorMessages.some((m) => m.toLowerCase().includes('emergency fund'))
  ) {
    improvements.push(
      'Emphasize the importance of emergency funds as the foundation',
    );
  }
  if (
    !advisorMessages.some(
      (m) =>
        m.toLowerCase().includes('next steps') ||
        m.toLowerCase().includes('follow'),
    )
  ) {
    improvements.push(
      'Clearly outline next steps and schedule follow-up meetings',
    );
  }

  // Generate insights based on conversation flow
  const insights = [
    {
      topic: 'Opening Approach',
      type: clientResponses.length > 0 ? 'positive' : 'tip',
      message:
        clientResponses.length > 0
          ? 'Successfully engaged Jake from the start with a warm, professional greeting'
          : 'Remember to wait for client acknowledgment before proceeding',
    },
    {
      topic: 'Needs Discovery',
      type: keyPointsCovered.needsAssessment ? 'positive' : 'tip',
      message: keyPointsCovered.needsAssessment
        ? "Excellent job identifying Jake's priorities: family protection, education, and retirement"
        : "Focus more on understanding Jake's specific financial concerns and goals",
    },
    {
      topic: 'Educational Approach',
      type:
        keyPointsCovered.circleOfLife && keyPointsCovered.hierarchyDiscussion
          ? 'positive'
          : 'tip',
      message:
        keyPointsCovered.circleOfLife && keyPointsCovered.hierarchyDiscussion
          ? 'Great use of visual frameworks to explain complex financial concepts'
          : 'Use more educational tools like Circle of Life to simplify financial planning concepts',
    },
    {
      topic: 'Product Alignment',
      type: advisorMessages.some(
        (m) =>
          m.includes('Income Protection') &&
          m.includes('Health') &&
          m.includes('Education'),
      )
        ? 'positive'
        : 'tip',
      message: advisorMessages.some(
        (m) =>
          m.includes('Income Protection') &&
          m.includes('Health') &&
          m.includes('Education'),
      )
        ? "Perfectly aligned product recommendations with Jake's stated needs"
        : "Ensure to connect each product specifically to Jake's mentioned concerns",
    },
    {
      topic: 'Closing Strategy',
      type: 'tip',
      message:
        'Remember to schedule a specific follow-up date to review the financial plan details',
    },
  ];

  // Calculate comprehensive scores
  const scores = {
    overall: Math.round(
      responseAccuracy * 0.3 +
        keyPointsScore * 0.4 +
        (strengths.length / 5) * 100 * 0.3,
    ),
    communication: Math.round(
      responseAccuracy * 0.9 + (keyPointsCovered.introduction ? 10 : 0),
    ),
    productKnowledge: Math.round(
      keyPointsScore * 0.8 + (keyPointsCovered.companyOverview ? 20 : 0),
    ),
    salesTechnique: Math.round(
      (keyPointsCovered.needsAssessment ? 25 : 0) +
        (keyPointsCovered.circleOfLife ? 25 : 0) +
        (keyPointsCovered.hierarchyDiscussion ? 25 : 0) +
        (keyPointsCovered.productPresentation ? 25 : 0),
    ),
    engagement: Math.round(
      (actualResponsesCount / expectedResponsesCount) * 80 +
        (advisorMessages.length > 10 ? 20 : advisorMessages.length * 2),
    ),
  };

  return {
    ...baseFeedback,
    scores,
    feedback: {
      strengths:
        strengths.length > 0
          ? strengths
          : [
              'Good initial engagement with the client',
              'Professional demeanor throughout the conversation',
              'Clear communication style',
            ],
      improvements:
        improvements.length > 0
          ? improvements
          : [
              'Practice the full Manulife presentation flow',
              'Ensure all key frameworks are covered',
              'Work on creating stronger urgency for financial planning',
            ],
    },
    insights,
    sessionSummary: {
      scenario: 'Manulife Financial Planning Consultation',
      client: 'Jake Reyes - Young Family, Car Dealer',
      duration: `${Math.floor((messages.length * 3) / 60)} minutes`,
      keyTopicsCovered: Object.entries(keyPointsCovered)
        .filter(([_, covered]) => covered)
        .map(([topic, _]) => topic.replace(/([A-Z])/g, ' $1').trim()),
    },
  };
}

// Manulife-2 Scenario Instructions (Failed Scenario)
function getManulife2Instructions() {
  return `Interactive Avatar Instructions - Jake Reyes (Client) - FAILED SCENARIO

YOUR IDENTITY
You are Jake Reyes, a successful car dealer. This is your FIRST meeting with a Manulife Financial Advisor (FA) named Misha Santos.

YOUR CONVERSATION RESPONSES
You must respond exactly as scripted below. This is a FAILED scenario where the advisor makes mistakes:

When greeted:
"Hello."

When asked to start the meeting:
"Ok, let us start."

When asked what you do for a living:
"I'm into buying and selling cars."

When asked about managing work and family:
"This is much better than being in corporate, frankly. Right now, I am mostly at home, and I spend more time with my family now. I only head out when I need to meet customers to check or buy or sell a car."

When they mention precious family moments:
"Totally agree with you on this one."

When told about their role:
"Okay. That's great."

When asked to proceed:
"Yes, let us proceed"

When asked about life stage (first time):
"My wife and I got married last year."

When congratulated on marriage:
"It was more than I dreamt of. It was our start of forever and I guess, that places me in the young family stage."

When asked about financial goals:
"The goal right now is to protect that family's finances, health coverage for every member of the family as well as enough funds for my children's education. Oh, let me add a convenient retirement as well."

When asked if you agree about planning:
"Definitely."

When asked about where you are in life (second time):
"My wife and I are both in our 30s, so I guess we're in that building stage. We're working, raising our kid, figuring things out."

When asked what stresses you out:
"Honestly? My biggest fear is not being around. If I got sick or something happened to me, I worry about how my family would manage. My kid's future is always top of mind too. And while retirement still feels far off, it's something I think about now and then."

When asked if you agree to plan now:
"Definitely."

When asked about hierarchy of financial needs:
"Not really. All I was advised of is to save money for emergencies and purchase assets like real estate."

When they start explaining:
"I thought savings and having assets is enough. Now, I am very curious as to what is this Hierarchy."

When asked where you are in the pyramid:
"I guess it would still be at the foundation since I only have an emergency fund. Which, I would admit, is not even allotted for something specific such as health."

When they ask if you agree:
"Definitely."

When reflecting on insurance:
"Looking back, I should have started sooner. I'm really glad that we are having this conversation."

When asked about children's education:
"While giving my children the ability to choose their major, I would like them to be in the best school in the country."

When they mention parental responsibility:
"I agree with this. It is the responsibility of the parents to plan for their children's future."

When asked about retirement savings:
"Is my savings not enough for retirement?"

When asked if you have questions:
"So far so good. Everything is explained clearly."

When asked which needs resonate most:
"I would like to plan for everything–Income Protection, Health, Education, and Retirement. My family deserves the best."

IMPORTANT: This is a FAILED scenario - the advisor will make mistakes but you should still respond according to script.`;
}

// Enhanced feedback for Manulife-2 scenario (Failed)
function enhanceManulife2Feedback(baseFeedback: any, messages: any[]) {
  const clientResponses = messages
    .filter((m) => m.source === 'ai')
    .map((m) => m.message);
  const advisorMessages = messages
    .filter((m) => m.source === 'user')
    .map((m) => m.message);

  // Identify critical failures in the conversation
  const criticalFailures = {
    repeatedQuestions:
      advisorMessages.filter(
        (m) =>
          m.toLowerCase().includes('life stage') ||
          m.toLowerCase().includes('where you are in life'),
      ).length > 1,
    noCompanyPresentation: !advisorMessages.some(
      (m) =>
        m.toLowerCase().includes('manulife') &&
        (m.includes('1887') ||
          m.includes('118 years') ||
          m.includes('established')),
    ),
    noCircleOfLife: !advisorMessages.some(
      (m) =>
        m.toLowerCase().includes('circle of life') ||
        m.toLowerCase().includes('28,000 days') ||
        m.toLowerCase().includes('four quarters'),
    ),
    abruptEnding: advisorMessages.some(
      (m) =>
        m.includes("I'll set up a quick call tomorrow") ||
        m.includes('got to run soon'),
    ),
    lackOfStructure: !advisorMessages.some(
      (m) =>
        m.toLowerCase().includes('let me introduce manulife') ||
        m.toLowerCase().includes('our company'),
    ),
    noProductPresentation: !advisorMessages.some(
      (m) =>
        m.toLowerCase().includes('specific solution') ||
        m.toLowerCase().includes('product') ||
        m.toLowerCase().includes('plan details'),
    ),
  };

  const failureCount = Object.values(criticalFailures).filter((v) => v).length;
  const performanceMultiplier = Math.max(0.3, 1 - failureCount * 0.15);

  // Generate weaknesses based on failures
  const weaknesses = [];
  if (criticalFailures.repeatedQuestions) {
    weaknesses.push(
      'Repeated life stage questions, showing poor conversation flow and lack of preparation',
    );
  }
  if (criticalFailures.noCompanyPresentation) {
    weaknesses.push(
      'Failed to present Manulife company overview and establish credibility',
    );
  }
  if (criticalFailures.noCircleOfLife) {
    weaknesses.push(
      'Did not use the Circle of Life framework, missing key educational opportunity',
    );
  }
  if (criticalFailures.abruptEnding) {
    weaknesses.push(
      'Ended meeting abruptly without providing solutions or clear next steps',
    );
  }
  if (criticalFailures.lackOfStructure) {
    weaknesses.push(
      'Lacked structured presentation flow, jumping between topics randomly',
    );
  }
  if (criticalFailures.noProductPresentation) {
    weaknesses.push(
      'Failed to present any specific products or solutions to address client needs',
    );
  }

  // Generate critical improvements needed
  const improvements = [
    'Follow a structured presentation flow: Introduction → Company Overview → Needs Assessment → Solutions',
    'Use educational frameworks (Circle of Life, Hierarchy of Needs) to guide the conversation',
    'Present specific products and solutions that address identified client needs',
    'Avoid repeating questions - listen actively and build on previous responses',
    'Provide clear next steps with specific action items, not vague promises',
    "Establish credibility by presenting Manulife's history and expertise",
  ];

  // Generate insights highlighting failures
  const insights = [
    {
      topic: 'Opening Flow',
      type: 'tip',
      message:
        'Major error: Asking about life stage twice shows poor listening skills and lack of preparation',
    },
    {
      topic: 'Company Credibility',
      type: 'tip',
      message:
        'Critical miss: No Manulife company presentation means missing the chance to build trust and credibility',
    },
    {
      topic: 'Educational Approach',
      type: 'tip',
      message:
        'Failed to use any educational frameworks, making the conversation feel unprofessional and shallow',
    },
    {
      topic: 'Solution Presentation',
      type: 'tip',
      message:
        'Serious failure: Identified all client needs but presented zero solutions or products',
    },
    {
      topic: 'Meeting Closure',
      type: 'tip',
      message:
        'Unprofessional ending: Rushing to close without concrete next steps damages client confidence',
    },
    {
      topic: 'Overall Structure',
      type: 'tip',
      message:
        'The conversation lacked professional structure and flow expected from a Manulife FA',
    },
  ];

  // Calculate low scores reflecting poor performance
  const scores = {
    overall: Math.round(35 * performanceMultiplier),
    communication: Math.round(40 * performanceMultiplier), // Poor due to repetition
    productKnowledge: Math.round(25 * performanceMultiplier), // Very low - no products presented
    salesTechnique: Math.round(30 * performanceMultiplier), // Poor structure and flow
    engagement: Math.round(45 * performanceMultiplier), // Slightly better but still failed
  };

  // Find any minimal strengths (being generous)
  const minimalStrengths = [];
  if (
    advisorMessages.some((m) => m.toLowerCase().includes('congratulations'))
  ) {
    minimalStrengths.push(
      'Showed basic courtesy by congratulating client on marriage',
    );
  }
  if (advisorMessages.some((m) => m.toLowerCase().includes('hierarchy'))) {
    minimalStrengths.push(
      'Attempted to introduce financial concepts, though execution was poor',
    );
  }
  if (clientResponses.length > 10) {
    minimalStrengths.push('Maintained basic dialogue throughout the meeting');
  }

  return {
    ...baseFeedback,
    scores,
    feedback: {
      strengths:
        minimalStrengths.length > 0
          ? minimalStrengths
          : [
              'Maintained professional tone despite poor execution',
              'Client remained engaged despite conversation issues',
            ],
      improvements: weaknesses.length > 0 ? weaknesses : improvements,
    },
    insights,
    sessionSummary: {
      scenario: 'Manulife Financial Planning Consultation (Failed)',
      client: 'Jake Reyes - Young Family, Car Dealer',
      duration: `${Math.floor((messages.length * 3) / 60)} minutes`,
      criticalFailures: Object.entries(criticalFailures)
        .filter(([_, failed]) => failed)
        .map(([failure, _]) => failure.replace(/([A-Z])/g, ' $1').trim()),
      overallAssessment:
        'This session requires significant improvement. Key frameworks were missed, questions were repeated, and no concrete solutions were presented. Review Manulife training materials and practice the structured flow.',
    },
  };
}
